/**
 * How an analytics tab names its date range in a subline ("acme.com, Sep 19
 * to Sep 25") and in a sentence ("this week"), plus the x-axis ticks a loading
 * chart can show before the data lands.
 */
import { format, subDays } from 'date-fns'

const MONTHLY_PERIODS = ['LAST_12_MONTHS', '12m', 'YEAR_TO_DATE', 'ytd']

export function formatRangeDay(raw: string | null, period: string): string {
  if (!raw) return ''
  const d = new Date(raw)
  if (Number.isNaN(d.getTime())) return raw
  if (MONTHLY_PERIODS.includes(period)) {
    return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' })
  }
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

/** "Sep 19 to Sep 25"; a single day reads once. */
export function formatRangeLabel(start: string | null, end: string | null, period: string): string {
  const a = formatRangeDay(start, period)
  const b = formatRangeDay(end, period)
  return a && b && a !== b ? `${a} to ${b}` : a || b
}

/** The period as a sentence fragment: "this week", "in the last 30 days". */
export function periodNoun(period: string): string {
  const map: Record<string, string> = {
    '1d': 'in the last 24 hours',
    '7d': 'this week',
    '30d': 'in the last 30 days',
    '90d': 'in the last 90 days',
    '180d': 'in the last 6 months',
    '365d': 'in the last 12 months',
    '12m': 'in the last 12 months',
    mtd: 'this month',
    ytd: 'this year',
  }
  return map[period] ?? 'in this range'
}

const DAY_WINDOWS: Record<string, number> = { '7d': 7, '30d': 30, '90d': 90 }

/**
 * Seven x-axis ticks for a loading chart, spread across a fixed day window
 * that ends today. Other periods (hourly, monthly, custom) return null and the
 * skeleton draws placeholder ticks instead, so a loading chart never names
 * dates the loaded chart will not show.
 */
export function loadingAxisTicks(period: string, now: Date, count = 7): string[] | null {
  const days = DAY_WINDOWS[period]
  if (!days) return null
  const step = (days - 1) / (count - 1)
  return Array.from({ length: count }, (_, i) =>
    format(subDays(now, Math.round((count - 1 - i) * step)), 'MMM d'),
  )
}
